/**
 * O histórico das revisões do perito (E152.F3, ADR-0103 §5.9), da mais recente à mais antiga, com a
 * versão que ele via, o fundamento e se a decisão está em vigor nesta versão. A decisão em vigor que
 * não está pendente de novo pode ser revista; nada aqui apaga ou altera o que foi registrado.
 *
 * E168.F5 (AJ-02, AJ-03): cada registro diz quem decidiu, e a cláusula localizada, onde o perito a
 * leu (documento, página, cláusula e trecho). E184.F3 (GUS-145 item 4): o aceite com fundamento diz
 * o valor lido, que o aceite não mudou.
 */
import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  LocalizacaoDaClausulaData,
  PainelDaFormalizacaoData,
  RevisaoEmVigorData,
} from '@/lib/formalizacaoAvaliacao';
import { revisaveis, revisoesVigentes } from '@/lib/formalizacaoRevisoes';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.formalizacaoAvaliacao;

function citada(l: LocalizacaoDaClausulaData): string {
  return t.clausulaCitada
    .replace('{clausula}', l.clausula ? t.clausulaDa.replace('{clausula}', l.clausula) : '')
    .replace('{documento}', l.documento)
    .replace('{pagina}', String(l.pagina))
    .replace('{trecho}', l.trecho);
}

export interface RevisoesRegistradasProps {
  painel: PainelDaFormalizacaoData;
  onRever: (revisao: RevisaoEmVigorData) => void;
}

export function RevisoesRegistradas({ painel, onRever }: RevisoesRegistradasProps): JSX.Element {
  const vigentes = revisoesVigentes(painel);
  const podeRever = new Map(
    revisaveis(painel.avaliacao.conteudo.avaliacao).map((r) => [r.objeto, r]),
  );
  return (
    <section
      aria-labelledby="formalizacao-registradas-titulo"
      data-testid="formalizacao-registradas"
    >
      <h4 id="formalizacao-registradas-titulo" className="text-body-md font-semibold text-fg">
        {t.registradas}
      </h4>
      {painel.revisoes.length === 0 ? (
        <p className="text-body-sm text-fg-muted">{t.semRegistradas}</p>
      ) : (
        <ol className="mt-sc-2 space-y-sc-2">
          {painel.revisoes.map((r) => {
            const vigente = vigentes.has(r.id);
            const revisavel = vigente ? podeRever.get(r.objeto) : undefined;
            return (
              <li
                key={r.id}
                className="space-y-sc-1 border-l-2 border-border pl-sc-3 text-body-sm"
                data-testid="formalizacao-registrada"
                data-vigente={vigente}
              >
                <p className="text-fg">
                  {t.tipoDaRevisao[r.tipo]} · {t.decisao[r.decisao]}{' '}
                  <Badge variant={vigente ? 'success' : 'neutral'}>
                    {vigente ? t.emVigor : t.semVigor}
                  </Badge>
                </p>
                <p className="text-fg-muted">
                  {t.sobreVersao
                    .replace('{versao}', String(r.avaliacao_versao))
                    .replace('{data}', formatarDataHora(r.criada_em))}
                  {r.revisor ? t.porRevisor.replace('{revisor}', r.revisor) : null}
                </p>
                {r.localizacao ? (
                  <p className="text-fg" data-testid="clausula-localizada">
                    {citada(r.localizacao)}
                  </p>
                ) : null}
                {r.valor_lido ? (
                  <p className="text-fg" data-testid="valor-lido-registrado">
                    {t.valorLido.replace('{valor}', r.valor_lido)}
                  </p>
                ) : null}
                <p className="whitespace-pre-line text-fg">{r.fundamento}</p>
                {revisavel ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onRever(revisavel)}
                  >
                    {t.rever}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
